import * as React from 'react';
import styled, { css } from 'styled-components';

import { FileItem } from '../FileItem';
import { Stack } from '../../layout/Stack';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface FileUploadExistingFile {
  id: string;
  name: string;
}

export interface FileUploadZoneProps {
  semanticId?: string;
  stagedFiles: File[];
  existingFiles?: FileUploadExistingFile[];
  onAdd: (files: File[]) => void;
  onRemoveStaged: (index: number) => void;
  onRemoveExisting?: (id: string) => void;
  multiple?: boolean;
  maxFiles?: number | null;
  accept?: string;
  fileType?: string;
  disabled?: boolean;
  error?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
}

function dashedBorderGradient(color: string): string {
  return `
    repeating-linear-gradient(0deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(90deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(180deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(270deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px)
  `;
}

const Wrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$disabled',
})<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  background-color: var(--ui-upload-bg, var(--ui-surface));
  padding: ${rem(16)} ${rem(8)};
  width: 100%;
  min-height: ${rem(100)};

  background-position: 0 0, 0 0, 100% 0, 0 100%;
  background-size: 1.5px 100%, 100% 1.5px, 1.5px 100%, 100% 1.5px;
  background-repeat: no-repeat;
  background-image: ${dashedBorderGradient('var(--ui-upload-border, var(--ui-border))')};

  &.is-invalid {
    background-image: ${dashedBorderGradient('var(--ui-upload-border-error, var(--ui-status-danger))')};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: var(--ui-background-muted);
      pointer-events: none;
      cursor: not-allowed;
      opacity: 1;
      color: var(--ui-text-subtle);
      min-height: auto;
      padding: ${rem(12)} ${rem(8)};
    `}
`;

const DisabledPlaceholder = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(40)};
  font-family: var(--ui-font-sans);
  font-size: ${rem(12)};
  color: var(--ui-text-subtle);
`;

const Dropzone = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${rem(5)};
  min-height: ${rem(72)};

  .material-symbols-outlined {
    color: var(--ui-upload-icon-color, var(--ui-interactive));
    width: ${rem(21)};
    height: ${rem(15)};
    flex-shrink: 0;
  }

  .upload-label {
    font-family: var(--ui-font-sans);
    font-weight: 400;
    font-size: ${rem(12)};
    line-height: ${rem(14)};
    color: var(--ui-text);
  }

  .upload-hint {
    font-family: var(--ui-font-sans);
    font-weight: 400;
    font-size: ${rem(12)};
    line-height: ${rem(14)};
    color: var(--ui-text);
  }

  input[type='file'] {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

export function FileUploadZone({
  semanticId,
  stagedFiles,
  existingFiles = [],
  onAdd,
  onRemoveStaged,
  onRemoveExisting,
  multiple = true,
  maxFiles = null,
  accept = '.pdf, .jpg, .jpeg, .png, .docx, .doc',
  fileType,
  disabled = false,
  error = false,
  name,
  onBlur,
  inputRef,
}: FileUploadZoneProps): JSX.Element {
  const semanticProps = getSemanticAttributes(semanticId);
  const allowsMultiple = multiple !== false;
  const effectiveMaxFiles = maxFiles ?? (allowsMultiple ? null : 1);
  const totalFiles = stagedFiles.length + existingFiles.length;
  const atLimit = effectiveMaxFiles != null && totalFiles >= effectiveMaxFiles;
  const canAdd = !disabled && !atLimit;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const list = event.target.files;
    if (!list?.length) return;
    onAdd(Array.from(list));
    event.target.value = '';
  };

  const hasFiles = existingFiles.length > 0 || stagedFiles.length > 0;
  const wrapperClass = [error ? 'is-invalid' : ''].filter(Boolean).join(' ') || undefined;

  return (
    <Wrapper
      className={wrapperClass}
      $disabled={disabled}
      data-ui-file-upload-zone=""
      {...semanticProps}
    >
      {hasFiles && (
        <Stack gap={rem(8)} align="stretch">
          {existingFiles.map((file) => (
            <FileItem
              key={`existing-${file.id}`}
              fileName={file.name}
              actions={
                onRemoveExisting && !disabled ? (
                  <FileItem.Action
                    icon="delete"
                    intent="danger"
                    label="Remove"
                    onClick={() => onRemoveExisting(file.id)}
                  />
                ) : undefined
              }
            />
          ))}
          {stagedFiles.map((file, index) => (
            <FileItem
              key={`${file.name}-${file.lastModified}-${index}`}
              fileName={file.name}
              actions={
                !disabled ? (
                  <FileItem.Action
                    icon="delete"
                    intent="danger"
                    label="Remove"
                    onClick={() => onRemoveStaged(index)}
                  />
                ) : undefined
              }
            />
          ))}
        </Stack>
      )}

      {disabled && !hasFiles && (
        <DisabledPlaceholder>No document uploaded</DisabledPlaceholder>
      )}

      {canAdd && (
        <Dropzone>
          <span className="material-symbols-outlined">backup</span>
          <div>
            <span className="upload-label">Click to Upload or Drag or Drop</span>
            <br />
            <span className="upload-hint">
              {fileType ?? '*PNG, JPG, JPEG, PDF, DOCX (Not more than 10 MB)'}
            </span>
          </div>
          <input
            ref={inputRef}
            type="file"
            name={name}
            disabled={disabled}
            multiple={allowsMultiple}
            accept={accept}
            onChange={handleChange}
            onBlur={onBlur}
            aria-invalid={error || undefined}
          />
        </Dropzone>
      )}
    </Wrapper>
  );
}

FileUploadZone.displayName = 'FileUploadZone';
